// ============================================================================
// LE TRACÉ D'UN EAN-13 — de treize chiffres à une suite de modules noirs et blancs.
//
// UTILITÉ : imprimer une étiquette, c'est dessiner des barres. `ean.ts` sait déjà VALIDER un code et
// en COMPOSER un ; il ne sait pas le DESSINER. Ce module ferme cet écart, et il le fait au même
// endroit — la règle du code-barres appartient au catalogue, pas à l'écran qui l'affiche.
//
// ⚠️ CE MODULE NE DESSINE RIEN LUI-MÊME. Il rend une suite de bits ; le SVG, le dégradé CSS ou la
// trame d'une imprimante thermique se construisent par-dessus. C'est ce qui lui permet de servir un
// aperçu à l'écran, une planche imprimée et, un jour, un pilote natif — sans se dédoubler.
//
// ⚠️ LA LARGEUR EST UNE CONTRAINTE PHYSIQUE, PAS UN GOÛT. Un EAN-13 fait 113 modules marges de
// silence comprises, et un module descend rarement sous 0,264 mm sans devenir illisible au scanner
// (80 % du nominal de 0,33 mm, plage GS1). D'où `eanQuality` : un code-barres trop étroit ne se voit
// PAS à l'écran — il se découvre en caisse, sur un article qu'on ne peut plus scanner.
// ============================================================================
import { EAN13_LENGTH } from './ean';

/**
 * Les trois alphabets de l'EAN-13, et la table de parité.
 *
 * ⚠️ CE SONT DES CONSTANTES DE NORME, pas des choix : elles viennent de la spécification GS1 et ne
 * se relisent pas au cas par cas. Les recopier ailleurs serait ouvrir la porte à une transposition
 * fautive qu'aucun test unitaire ne remarquerait — un chiffre encodé de travers donne un code qui
 * s'imprime, se scanne, et rend un AUTRE produit.
 */
const LEFT_ODD = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011'] as const;
const LEFT_EVEN = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111'] as const;
const RIGHT = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100'] as const;
/** Le premier chiffre ne s'encode pas : il se LIT dans la parité des six suivants. */
const PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL'] as const;

/** Modules d'un EAN-13 complet, marges de silence COMPRISES. */
export const EAN13_MODULES = 113;
/** Largeur nominale d'un module (mm) — la référence GS1 à 100 %. */
export const EAN13_MODULE_NOMINAL_MM = 0.33;
/** Largeur MINIMALE d'un module (mm) — 80 % du nominal, plancher de lisibilité. */
export const EAN13_MODULE_MIN_MM = 0.264;
/** Largeur minimale d'un EAN-13 SCANNABLE, en millimètres. */
export const EAN13_MIN_WIDTH_MM = EAN13_MODULES * EAN13_MODULE_MIN_MM;

/**
 * Les 95 modules du symbole, en bits (`1` = barre, `0` = espace).
 *
 * ⚠️ RETOURNE `null` PLUTÔT QU'UN SYMBOLE APPROXIMATIF quand le code n'a pas treize chiffres. Un
 * code-barres « presque juste » est le pire cas : il s'imprime, il se scanne, et il désigne autre
 * chose. L'absence de barres, elle, se voit tout de suite.
 * ⚠️ LA CLÉ DE CONTRÔLE N'EST PAS VÉRIFIÉE ICI, délibérément : `isValidEan13` existe pour ça, et un
 * appelant peut vouloir DESSINER un code repris tel quel pour le montrer à l'écran. Séparer « ce que
 * ça vaut » de « à quoi ça ressemble » laisse chacun refuser à son niveau.
 * ⚠️ 95 MODULES ICI, 113 AVEC LES MARGES : les marges de silence ne sont pas des barres, elles ne
 * s'encodent donc pas — mais elles comptent dans la largeur physique (cf. `EAN13_MODULES`).
 */
export function ean13Bits(code: string): string | null {
  const digits = String(code ?? '').replace(/[^0-9]/g, '');
  if (digits.length !== EAN13_LENGTH) return null;
  const d = digits.split('').map(Number) as number[];
  const parity = PARITY[d[0]!]!;
  let bits = '101'; // garde gauche
  for (let i = 0; i < 6; i++) {
    const table = parity[i] === 'L' ? LEFT_ODD : LEFT_EVEN;
    bits += table[d[i + 1]!]!;
  }
  bits += '01010'; // garde centrale
  for (let i = 7; i < 13; i++) bits += RIGHT[d[i]!]!;
  return `${bits}101`; // garde droite
}

/** Une plage de modules de même couleur — de quoi dessiner sans repasser bit à bit. */
export interface EanRun {
  /** Position de départ, en fraction du symbole (0 → 1). */
  from: number;
  /** Position de fin, en fraction du symbole. */
  to: number;
  /** `true` = barre (encre), `false` = espace. */
  bar: boolean;
}

/**
 * Les bits regroupés en PLAGES — la forme qu'attendent tous les rendus.
 *
 * ⚠️ EN FRACTIONS ET NON EN PIXELS OU EN MILLIMÈTRES : c'est ce qui rend le résultat utilisable par
 * un aperçu à l'écran, un dégradé CSS d'impression et un pilote thermique, sans qu'aucun des trois
 * n'impose son unité aux deux autres.
 */
export function ean13Runs(code: string): EanRun[] | null {
  const bits = ean13Bits(code);
  if (!bits) return null;
  const n = bits.length;
  const runs: EanRun[] = [];
  let i = 0;
  while (i < n) {
    let j = i;
    while (j < n && bits[j] === bits[i]) j++;
    runs.push({ from: i / n, to: j / n, bar: bits[i] === '1' });
    i = j;
  }
  return runs;
}

/** Le verdict de lisibilité d'un code-barres à une largeur donnée. */
export interface EanQuality {
  /** Assez large pour être scanné de façon fiable ? */
  scannable: boolean;
  /** Largeur d'un module, en millimètres. */
  moduleMm: number;
  /** Pourcentage de la taille nominale (100 % = 0,33 mm par module). */
  pct: number;
}

/**
 * CE QUE VAUT UN CODE-BARRES À CETTE LARGEUR — la garde la plus utile du module Étiquettes.
 *
 * ⚠️ ELLE MESURE, ELLE NE JUGE PAS DE L'INTENTION. Un code sous le plancher n'est pas refusé par ce
 * cœur : il est SIGNALÉ. C'est l'écran qui décide quoi en faire — et c'est bien ainsi, parce que le
 * même symbole peut être acceptable sur une étiquette lue à 5 cm et inacceptable en rayon.
 * ⚠️ AUCUN MESSAGE ICI : les phrases appartiennent à l'écran, qui parle à un opérateur dans SA
 * langue. Le cœur rend un chiffre et un verdict.
 */
export function eanQuality(widthMm: number): EanQuality {
  const moduleMm = widthMm / EAN13_MODULES;
  return {
    scannable: moduleMm >= EAN13_MODULE_MIN_MM,
    moduleMm,
    pct: Math.round((moduleMm / EAN13_MODULE_NOMINAL_MM) * 100),
  };
}
